import ResponsiveTable from '../../components/ResponsiveTable';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../utils/api';
import RecentOptions from '../../components/RecentOptions';
import { recordRecentPick } from '../../utils/recentPicks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CopyLinkButton from '../../components/CopyLinkButton';
import AssigneePopup from '../../components/AssigneePopup';
import {
    faClipboardList, faPlus, faSearch, faEdit, faTrash,
    faPaperclip, faClipboardCheck, faTimes, faFolderOpen, faBuilding, faRepeat, faUserPen, faBell, faLock
} from '@fortawesome/free-solid-svg-icons';
import Pagination from '../../components/Pagination';
import RecurringTaskList from '../../components/RecurringTaskList';
import NudgeModal from '../../components/NudgeModal';
import SelfAssignedList from '../../components/SelfAssignedList';
import { slug, taskContextLabel } from '../../utils/taskHelpers';
import Avatar from '../../components/Avatar';
import { useRecentSearches } from '../../components/RecentSearches';
import TaskCountdown, { hasTimeWindow } from '../../components/TaskCountdown';
import '../../styles/App.css';
import '../../styles/tasks.css';
import '../../styles/selftask.css';

// fromDate/toDate ride along in the same object so they reach /tasks/managed
// with the rest and stack with them server-side. Empty means no date limit, so
// this list still opens on everything — unlike My Tasks, which opens on today.
const DEFAULT_FILTERS = {
    status: 'All', priority: 'All', projectId: 'All', taskType: 'All',
    assignedBy: 'All', assigneeId: 'All',
    fromDate: '', toDate: ''
};

// The filters that are plain strings in the URL; the toggles are handled by name.
const FILTER_KEYS = Object.keys(DEFAULT_FILTERS);

// The progress bar reflects the task's shared status rather than a headcount.
const STATUS_PROGRESS = {
    'Pending': 0,
    'In Progress': 50,
    'On Hold': 50,
    'Completed': 100
};

const Tasks = () => {
    const navigate = useNavigate();
    const currentUser = JSON.parse(localStorage.getItem('user'));
    const currentUserId = currentUser?.id || currentUser?._id;
    const isPrivileged = ['ADMIN', 'HR'].includes(currentUser?.role);
    const [searchParams, setSearchParams] = useSearchParams();

    // 'tasks' lists work with its people attached; 'recurring' shows the daily
    // schedules rather than the individual tasks they generate. The
    // people-first view ("who has nothing assigned") moved to its own page,
    // Task Report, alongside the calendar.
    const VIEWS = ['tasks', 'recurring', 'self'];
    const [view, setView] = useState(() => {
        const v = searchParams.get('view');
        return VIEWS.includes(v) ? v : 'tasks';
    });

    // The task a quick nudge is being composed for, straight from the row —
    // chasing someone should not require opening the task first.
    const [nudgeTarget, setNudgeTarget] = useState(null);
    // Which task's people are being looked at, or null. Holds the task
    // rather than the list so the popup can title itself.
    const [peopleOf, setPeopleOf] = useState(null);

    // Drives the amber count on the Self-Assigned tab, so a manager can see
    // there is something waiting without opening it.
    const [pendingSelf, setPendingSelf] = useState(0);

    useEffect(() => {
        api.get('/tasks/self-assigned', { params: { approvalStatus: 'Pending', limit: 1 } })
            .then(res => setPendingSelf(res.data.pendingCount || 0))
            .catch(() => setPendingSelf(0));
    }, [view]);

    const [tasks, setTasks] = useState([]);
    const [projectsList, setProjectsList] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filters, the toggles, search and page all start from the URL, so they
    // survive a refresh, coming back from a task, and a pasted link.
    const [filters, setFilters] = useState(() => {
        const initial = { ...DEFAULT_FILTERS };
        FILTER_KEYS.forEach(k => { if (searchParams.get(k)) initial[k] = searchParams.get(k); });
        return initial;
    });
    const [assignedByMe, setAssignedByMe] = useState(() => searchParams.get('byMe') === '1');
    const [overdueOnly, setOverdueOnly] = useState(() => searchParams.get('overdue') === '1');
    const [searchTerm, setSearchTerm] = useState(() => searchParams.get('q') || '');
    const recentSearch = useRecentSearches('task-management', { value: searchTerm, onPick: setSearchTerm });
    const [debouncedSearch, setDebouncedSearch] = useState(() => searchParams.get('q') || '');
    const [assignersList, setAssignersList] = useState([]);
    const [employeesList, setEmployeesList] = useState([]);

    const [currentPage, setCurrentPage] = useState(() => parseInt(searchParams.get('page')) || 1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);


    useEffect(() => {
        api.get('/tasks/assigners').then(res => setAssignersList(res.data || [])).catch(() => setAssignersList([]));
        api.get('/tasks/assignable-employees').then(res => setEmployeesList(res.data || [])).catch(() => setEmployeesList([]));
    }, []);

    useEffect(() => {
        api.get('/tasks/assignable-projects')
            .then(res => setProjectsList(res.data || []))
            .catch(() => setProjectsList([]));
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchTerm), 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Any change to what is being asked for starts again from page 1 — but not
    // on first render, or a page restored from the URL would be thrown away.
    const firstRender = useRef(true);
    useEffect(() => {
        if (firstRender.current) { firstRender.current = false; return; }
        setCurrentPage(1);
    }, [debouncedSearch, filters, assignedByMe, overdueOnly]);

    // Mirror the state into the URL (replace, so Back isn't a keystroke log).
    useEffect(() => {
        const next = new URLSearchParams();
        ['view', 'task'].forEach(k => { if (searchParams.get(k)) next.set(k, searchParams.get(k)); });
        FILTER_KEYS.forEach(k => { if (filters[k] && filters[k] !== DEFAULT_FILTERS[k]) next.set(k, filters[k]); });
        if (assignedByMe) next.set('byMe', '1');
        if (overdueOnly) next.set('overdue', '1');
        if (debouncedSearch) next.set('q', debouncedSearch);
        if (currentPage > 1) next.set('page', String(currentPage));
        if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    }, [filters, assignedByMe, overdueOnly, debouncedSearch, currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchTasks = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/tasks/managed', {
                params: {
                    page: currentPage,
                    limit: itemsPerPage,
                    search: debouncedSearch,
                    // Days generated by a schedule belong to the Recurring tab;
                    // listing them here too would make the split meaningless.
                    excludeRecurring: true,
                    // The only date mode this list offers. With both ends
                    // empty the server applies no date limit at all.
                    filterType: 'Custom',
                    ...filters,
                    // Only sent when on, so the server sees nothing otherwise.
                    ...(assignedByMe ? { assignedByMe: true } : {}),
                    ...(overdueOnly ? { overdue: true } : {})
                }
            });
            setTasks(res.data.data);
            setTotalPages(res.data.pagination.totalPages);
            setTotalRecords(res.data.pagination.totalRecords);
        } catch (err) {
            console.error('Failed to load tasks', err);
            Swal.fire('Error', 'Could not load tasks.', 'error');
        } finally {
            setLoading(false);
        }
    }, [currentPage, itemsPerPage, debouncedSearch, filters, assignedByMe, overdueOnly]);

    // The employee view loads its own data, so don't pay for this one while it's up.
    useEffect(() => {
        if (view === 'tasks') fetchTasks();
    }, [fetchTasks, view]);

    // Older notifications link here as /tasks?task=<id>; send them to the page.
    useEffect(() => {
        const taskId = searchParams.get('task');
        if (taskId) navigate(`/task/${taskId}`, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFilterChange = (e) =>
        setFilters({ ...filters, [e.target.name]: e.target.value });

    const clearFilters = () => {
        setFilters(DEFAULT_FILTERS);
        setAssignedByMe(false);
        setOverdueOnly(false);
        setSearchTerm('');
    };

    // From and To are two halves of one date range, so they count once
    // between them rather than twice.
    const activeFilterCount =
        Object.keys(DEFAULT_FILTERS)
            .filter(k => !['fromDate', 'toDate'].includes(k) && filters[k] !== 'All').length +
        (filters.fromDate || filters.toDate ? 1 : 0) +
        (assignedByMe ? 1 : 0) + (overdueOnly ? 1 : 0) +
        (searchTerm ? 1 : 0);

    const handleDelete = async (task) => {
        const result = await Swal.fire({
            title: 'Remove this task?',
            text: `"${task.title}" will be archived and disappear from everyone's list. `
                + 'Its files, proof and discussion attachments are deleted permanently.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            confirmButtonText: 'Yes, remove it'
        });
        if (!result.isConfirmed) return;

        try {
            await api.delete(`/tasks/${task._id}`);
            Swal.fire({
                icon: 'success', title: 'Task removed', toast: true,
                position: 'top-end', timer: 1800, showConfirmButton: false
            });
            fetchTasks();
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Could not remove the task.', 'error');
        }
    };

    const isOverdue = (task) =>
        new Date(task.overdueAt || task.dueDate) < new Date() && !['Completed', 'On Hold'].includes(task.status);

    return (
        <div className="attendance-container fade-in">
            <div className="task-page-header">
                <h1 className="page-title header-no-margin">
                    <FontAwesomeIcon icon={faClipboardList} style={{ marginRight: '10px', color: 'light-dark(#215D7B, var(--theme-accent-ink))' }} />
                    {isPrivileged ? 'All Tasks' : 'Assigned Tasks'}
                </h1>
                {/* Two kinds of task sit together in one group. Checking who is
                    carrying work is a different question, so it stands apart. */}
                <div className="task-view-group">
                    <div className="type-toggle view-toggle">
                        <button
                            type="button"
                            className={`type-toggle-btn ${view === 'tasks' ? 'active' : ''}`}
                            onClick={() => setView('tasks')}
                        >
                            <FontAwesomeIcon icon={faClipboardList} /> Regular Tasks
                        </button>
                        <button
                            type="button"
                            className={`type-toggle-btn ${view === 'recurring' ? 'active' : ''}`}
                            onClick={() => setView('recurring')}
                        >
                            <FontAwesomeIcon icon={faRepeat} /> Recurring Tasks
                        </button>
                        <button
                            type="button"
                            className={`type-toggle-btn ${view === 'self' ? 'active' : ''}`}
                            onClick={() => setView('self')}
                        >
                            <FontAwesomeIcon icon={faUserPen} /> Self-Assigned
                            {pendingSelf > 0 && <span className="sa-tab-count">{pendingSelf}</span>}
                        </button>
                    </div>
                </div>

                <button className="gts-btn primary" style={{ marginLeft: 'auto' }} onClick={() => navigate('/add-task')}>
                    <FontAwesomeIcon icon={faPlus} /> Assign Task
                </button>
            </div>

            {view === 'recurring' ? <RecurringTaskList /> : view === 'self' ? <SelfAssignedList /> : (
            <>

            {/* --- Search + filters in a single bar --- */}
            <div className="task-toolbar">
                <div className="task-toolbar-search">
                    <FontAwesomeIcon icon={faSearch} className="task-search-icon" />
                    <input
                        {...recentSearch.inputProps}
                        type="text"
                        placeholder="Search task, assignee, assigned-by, project or type..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {recentSearch.menu}
                </div>

                <div className="task-toolbar-filters">
                    <select
                        className={`task-filter-select ${filters.status !== 'All' ? 'is-active' : ''}`}
                        name="status" value={filters.status} onChange={handleFilterChange}
                    >
                        <option value="All">All Statuses</option>
                        <option value="Pending">Pending</option>
                        <option value="In Progress">In Progress</option>
                        <option value="On Hold">On Hold</option>
                        <option value="Completed">Completed</option>
                    </select>

                    <select
                        className={`task-filter-select ${filters.priority !== 'All' ? 'is-active' : ''}`}
                        name="priority" value={filters.priority} onChange={handleFilterChange}
                    >
                        <option value="All">All Priorities</option>
                        <option value="Low">Low</option>
                        <option value="Medium">Medium</option>
                        <option value="High">High</option>
                        <option value="Urgent">Urgent</option>
                    </select>

                    <select
                        className={`task-filter-select ${filters.taskType !== 'All' ? 'is-active' : ''}`}
                        name="taskType" value={filters.taskType} onChange={handleFilterChange}
                    >
                        <option value="All">All Types</option>
                        <option value="Project Task">Project Task</option>
                        <option value="Regular Office Task">Regular Office</option>
                    </select>

                    {/* The dropdown is meaningless while "by me" pins the assigner. */}
                    <select
                        className={`task-filter-select ${filters.assignedBy !== 'All' ? 'is-active' : ''}`}
                        name="assignedBy" value={filters.assignedBy} onChange={handleFilterChange}
                        disabled={assignedByMe}
                        title={assignedByMe ? 'Turn off "Assigned by me" to pick someone else' : undefined}
                    >
                        <option value="All">Assigned by: Anyone</option>
                        {assignersList.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
                    </select>

                    <select
                        className={`task-filter-select ${filters.assigneeId !== 'All' ? 'is-active' : ''}`}
                        name="assigneeId" value={filters.assigneeId} onChange={(e) => { handleFilterChange(e); recordRecentPick('task-assignee', e.target.value); }}
                    >
                        <option value="All">All Assignees</option>
                        <RecentOptions kind="task-assignee" items={employeesList} />
                    </select>

                    <select
                        className={`task-filter-select ${filters.projectId !== 'All' ? 'is-active' : ''}`}
                        name="projectId" value={filters.projectId} onChange={(e) => { handleFilterChange(e); recordRecentPick('project', e.target.value); }}
                    >
                        <option value="All">All Projects</option>
                        <RecentOptions kind="project" items={projectsList} />
                    </select>

                    {/* Either end may be left empty for an open-ended range —
                        "everything since the 1st" is as useful as a closed one. */}
                    <div className="task-filter-dates">
                        <label className="task-filter-date">
                            <span>From</span>
                            <input
                                type="date"
                                name="fromDate"
                                className={filters.fromDate ? 'is-active' : ''}
                                value={filters.fromDate}
                                max={filters.toDate || undefined}
                                onChange={handleFilterChange}
                            />
                        </label>
                        <label className="task-filter-date">
                            <span>To</span>
                            <input
                                type="date"
                                name="toDate"
                                className={filters.toDate ? 'is-active' : ''}
                                value={filters.toDate}
                                min={filters.fromDate || undefined}
                                onChange={handleFilterChange}
                            />
                        </label>
                    </div>

                    <label className={`task-filter-toggle ${assignedByMe ? 'is-active' : ''}`}>
                        <input type="checkbox" checked={assignedByMe} onChange={(e) => setAssignedByMe(e.target.checked)} />
                        <span>Assigned by me</span>
                    </label>
                    <label className={`task-filter-toggle ${overdueOnly ? 'is-active' : ''}`}>
                        <input type="checkbox" checked={overdueOnly} onChange={(e) => setOverdueOnly(e.target.checked)} />
                        <span>Overdue only</span>
                    </label>

                    {activeFilterCount > 0 && (
                        <button className="task-filter-clear"onClick={clearFilters}>
                            <FontAwesomeIcon icon={faTimes} /> Clear ({activeFilterCount})
                        </button>
                    )}
                </div>
            </div>

            {!loading && totalRecords > 0 && (
                <div className="task-result-count">
                    Showing {tasks.length} of {totalRecords} task{totalRecords === 1 ? '' : 's'}
                    {activeFilterCount > 0 && ' (filtered)'}
                </div>
            )}

            {loading ? (
                <div className="control-card text-center" style={{ padding: '50px 20px', color: 'light-dark(#64748b, var(--theme-muted))' }}>
                    Loading tasks...
                </div>
            ) : tasks.length === 0 ? (
                <div className="control-card text-center" style={{ padding: '60px 20px' }}>
                    <FontAwesomeIcon icon={faClipboardCheck} style={{ fontSize: '3.5rem', color: '#cbd5e1', marginBottom: '15px' }} />
                    <h3 style={{ color: 'light-dark(#475569, var(--theme-body))' }}>
                        {activeFilterCount > 0 ? 'No matching tasks' : 'No tasks yet'}
                    </h3>
                    <p className="text-muted">
                        {activeFilterCount > 0
                            ? 'Try clearing your filters to see everything.'
                            : 'Assign your first task to get started. Daily repeating work lives under Recurring Tasks.'}
                    </p>
                    {activeFilterCount > 0 && (
                        <button className="gts-btn secondary" style={{ marginTop: '12px' }} onClick={clearFilters}>
                            <FontAwesomeIcon icon={faTimes} /> Clear filters
                        </button>
                    )}
                </div>
            ) : (
                <div className="employee-table-container">
                    <ResponsiveTable className="employee-table task-table">
                        <thead>
                            <tr>
                                <th className="col-task">Task</th>
                                <th className="col-project">Type / Project</th>
                                <th className="col-assignees">Assignees</th>
                                <th className="col-priority">Priority</th>
                                <th className="col-due">Due Date</th>
                                <th className="col-progress">Progress</th>
                                <th className="col-actions">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tasks.map(task => {
                                const pct = STATUS_PROGRESS[task.status] ?? 0;

                                return (
                                    <tr key={task._id} style={{ cursor: 'pointer' }} onClick={() => { recentSearch.save(); navigate(`/task/${task._id}`); }}>
                                        <td data-label="Task" className="col-task">
                                            <span className="task-title-cell" title={task.title}>{task.title}</span>
                                            <span className="task-title-sub">
                                                <span className={`task-status-badge ${slug(task.status)}`}>
                                                    {task.status}
                                                </span>
                                                {task.isSelfAssigned && (
                                                    <span className="ap-badge self" title="The employee logged this themselves">
                                                        <FontAwesomeIcon icon={faUserPen} /> Self Assigned
                                                    </span>
                                                )}
                                                {task.isPrivate && (
                                                    <span className="ap-badge private" title="Only the assignees and the assigner can see this task">
                                                        <FontAwesomeIcon icon={faLock} /> Private
                                                    </span>
                                                )}
                                                {task.attachments?.length > 0 && (
                                                    <span className="task-media-count">
                                                        <FontAwesomeIcon icon={faPaperclip} /> {task.attachments.length}
                                                    </span>
                                                )}
                                            </span>
                                        </td>

                                        {/* Holds either a project name or "Regular Office",
                                            so the icon carries the distinction. */}
                                        <td data-label="Type / Project" className="col-project">
                                            <span className={`task-context ${task.taskType === 'Regular Office Task' ? 'is-office' : ''}`}>
                                                <FontAwesomeIcon icon={task.taskType === 'Regular Office Task' ? faBuilding : faFolderOpen} />
                                                <span className="task-context-label">{taskContextLabel(task)}</span>
                                            </span>
                                        </td>

                                        <td data-label="Assignees" className="col-assignees">
                                            <div
                                                className="avatar-stack is-clickable"
                                                title="See who is working on this"
                                                role="button"
                                                tabIndex={0}
                                                /* The row navigates to the task, so this must not
                                                   bubble or the popup would open behind a page
                                                   change. */
                                                onClick={(e) => { e.stopPropagation(); setPeopleOf(task); }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault(); e.stopPropagation(); setPeopleOf(task);
                                                    }
                                                }}
                                            >
                                                {task.assignees.slice(0, 4).map(a => (
                                                    <Avatar key={a._id} name={a?.name} profilePic={a?.profilePic} className="assignee-avatar" />
                                                ))}
                                                {task.assignees.length > 4 && (
                                                    <div className="assignee-avatar avatar-more">+{task.assignees.length - 4}</div>
                                                )}
                                            </div>
                                            {task.assignedBy?.name && (
                                                <span className="assignee-by-note" title={`Assigned by ${task.assignedBy.name}`}>
                                                    by {task.assignedBy.name}
                                                </span>
                                            )}
                                        </td>

                                        <td data-label="Priority" className="col-priority">
                                            <span className={`priority-badge ${slug(task.priority)}`}>{task.priority}</span>
                                        </td>

                                        <td data-label="Due Date" className="col-due">
                                            <span className={`task-due-date ${isOverdue(task) ? 'overdue' : ''}`}>
                                                {new Date(task.dueDate).toLocaleDateString('en-GB')}
                                            </span>
                                            {hasTimeWindow(task) && <TaskCountdown task={task} compact />}
                                        </td>

                                        <td data-label="Progress" className="col-progress">
                                            <div className="task-progress">
                                                <div className="task-progress-track">
                                                    <div
                                                        className={`task-progress-fill ${slug(task.status)}`}
                                                        style={{ width: `${pct}%` }}
                                                    />
                                                </div>
                                                <span className="task-progress-label">{pct}%</span>
                                            </div>
                                        </td>

                                        <td
                                            data-label="Actions"
                                            className="col-actions task-actions-cell"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <div className="task-actions-inner">
                                                {/* Only worth offering on work that is still
                                                    running and has somebody other than you on it. */}
                                                {task.status !== 'Completed' &&
                                                    task.assignees.some(a => a?._id !== currentUserId) && (
                                                        <button
                                                            className="icon-btn" title="Nudge for an update" aria-label="Nudge for an update"
                                                            onClick={() => setNudgeTarget(task)}
                                                        >
                                                            <FontAwesomeIcon icon={faBell} />
                                                        </button>
                                                    )}
                                                <CopyLinkButton
                                                    path={`/task/${task._id}`}
                                                    label="Copy link to this task"
                                                />
                                                <button
                                                    className="icon-btn" title="Edit task" aria-label="Edit task"
                                                    onClick={() => navigate(`/edit-task/${task._id}`)}
                                                >
                                                    <FontAwesomeIcon icon={faEdit} />
                                                </button>
                                                <button
                                                    className="icon-btn danger" title="Remove task" aria-label="Remove task"
                                                    onClick={() => handleDelete(task)}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </ResponsiveTable>
                </div>
            )}

            {!loading && totalRecords > 0 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    limit={itemsPerPage}
                    onPageChange={setCurrentPage}
                    onLimitChange={(val) => { setItemsPerPage(val); setCurrentPage(1); }}
                />
            )}

            </>
            )}

            {nudgeTarget && (
                <NudgeModal
                    task={nudgeTarget}
                    currentUserId={currentUserId}
                    onClose={() => setNudgeTarget(null)}
                />
            )}

            <AssigneePopup
                open={Boolean(peopleOf)}
                onClose={() => setPeopleOf(null)}
                title="Working on this"
                subtitle={peopleOf?.title}
                people={peopleOf?.assignees || []}
            />

        </div>
    );
};

export default Tasks;
